import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessToken } from '../../awsAuth';
import { Helmet } from "react-helmet-async";
import './SearchResults.scss';

import filterIcon from './img/filter.png';
import FilterModal from './FilterModal';
import ShoppingRefinementBar from './ShoppingRefinementBar';
import SearchHero from './SearchHero';
import CvantoAgentModal from './CvantoAgentModal';
import { searchPage as agentSearchPage } from '../CvantoAI/cvantoAIApi';
import SearchModeHeader from './SearchModeHeader';
import PeoplePreviewRow from './PeoplePreviewRow';
import ListingsPreviewGrid from './ListingsPreviewGrid';
import BuyerRequestSuggestion from './BuyerRequestSuggestion';
import { parseAttrParamsFromSearch, buildAttrURLEntries } from './attrFilterParams';
import { nameToSlug } from '../../utils/categoriesMap';
import { resolveSearchCanonical } from './searchCanonical';
import DynamicHeader from "../Header/DynamicHeader";
import AdSlot from "../Ads/AdSlot";
import addIcon from "../Sidebar/img/add.png";
import {
  trackSearchQuery,
  trackOpenProfile,
  trackOpenListingDetails,
} from "../../utils/trackActivity";

// Pure listing-display helpers moved to their own module (listingDisplay.js)
// so the new "Все"-tab preview grid (ListingsPreviewGrid.js) can reuse them
// without a circular import with this file. Re-exported here unchanged so
// existing imports/tests (e.g. shouldShowBoostBadge.test.js's
// `import { shouldShowBoostBadge } from "./SearchResults"`) keep working.
import {
  DEFAULT_LISTING_IMG,
  getStablePosterTheme,
  getPosterTitleSizeClass,
  getListingThumb,
  isWantedListing,
  shouldShowBoostBadge,
  formatCurrencyLabel,
} from './listingDisplay';
import { thumbImgProps, fallbackToOriginal } from "../../utils/imageUrl";

export {
  getStablePosterTheme,
  getPosterTitleSizeClass,
  getListingThumb,
  isWantedListing,
  shouldShowBoostBadge,
  formatCurrencyLabel,
};

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";


const TTL_MIN = 30 * 1000;           
const TTL_SHORT = 2 * 60 * 1000;     
const TTL_MED = 5 * 60 * 1000;       
const TTL_LONG = 10 * 60 * 1000;     
const TTL_MAX = 15 * 60 * 1000;      

const clampTTL = (ms) => Math.max(TTL_MIN, Math.min(TTL_MAX, ms));

const computeSmartTTL = ({
  kind,          
  tab,           
  q,
  category,
  sort,
  filtersActive,
  hasAnyUserContext, 
}) => {
  const qStr = String(q || '').trim();
  const qLen = qStr.length;

  const hasQ = qLen > 0;
  const hasMeaningfulQ = qLen >= 3;

  const hasCategory = !!String(category || '').trim();
  const sortKey = String(sort || 'new');
  const isNewSort = sortKey === 'new';

  let ttl = tab === 'all' ? TTL_SHORT : TTL_MED;

  if (kind === 'users') {
    ttl = tab === 'all' ? TTL_MED : TTL_LONG;
    if (!hasQ && !filtersActive) ttl = TTL_SHORT;
    if (hasMeaningfulQ || filtersActive) ttl = TTL_LONG;
    if (hasAnyUserContext && !hasMeaningfulQ && !filtersActive) ttl = Math.max(TTL_MIN, ttl - 30 * 1000);
    return clampTTL(ttl);
  }

  ttl = tab === 'all' ? TTL_SHORT : TTL_MED;

  if (isNewSort && !hasQ && !filtersActive && !hasCategory) ttl = TTL_MIN;

  if (hasMeaningfulQ) ttl = TTL_MED;
  if (hasCategory || filtersActive) ttl = TTL_LONG;

  if (!isNewSort) ttl = Math.max(ttl, TTL_MED);

  return clampTTL(ttl);
};

const LOCATION_STOPWORDS = new Set([
  "область","обл","район","р-н","місто","м","м.","село","с","с.","смт","сел","громада",
  "district","region","oblast","rayon","city","village","town"
]);

const normalizeLocationTokens = (s) => {
  const str = String(s || "")
    .toLowerCase()
    .replace(/[`"'’]/g, "")
    .replace(/[.,/\\|()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!str) return [];

  return str
    .split(" ")
    .map((t) => t.trim())
    .filter(Boolean)
    .filter((t) => !LOCATION_STOPWORDS.has(t))
    .filter((t) => t.length >= 2);
};

const locationSoftMatch = (itemLocation, filterLocation) => {
  const a = normalizeLocationTokens(itemLocation);
  const b = normalizeLocationTokens(filterLocation);

  if (b.length === 0) return true;
  if (a.length === 0) return false;

  const aStr = a.join(" ");
  const bStr = b.join(" ");

  if (aStr.includes(bStr) || bStr.includes(aStr)) return true;

  for (const t of b) {
    if (t.length >= 3 && aStr.includes(t)) return true;
  }
  for (const t of a) {
    if (t.length >= 3 && bStr.includes(t)) return true;
  }

  return false;
};

const getLocationQueryForAPI = (filterLocation) => {
  const tokens = normalizeLocationTokens(filterLocation);
  if (!tokens.length) return "";
  return tokens[0] || "";
};

function safeLoadCache(key) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.expires && parsed.expires < Date.now()) {
      window.localStorage.removeItem(key);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function safeSaveCache(key, data, ttlMs) {
  if (typeof window === 'undefined') return;
  try {
    const ttl = typeof ttlMs === 'number' ? ttlMs : TTL_MED;
    const payload = {
      data,
      expires: Date.now() + ttl,
      ttl,
      savedAt: Date.now(),
    };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch {
  }
}

function spreadSameAuthorAds(items = []) {
  const arr = Array.isArray(items) ? [...items] : [];

  const getAuthorId = (item) => String(item?.userId || item?.authorId || "");

  for (let i = 1; i < arr.length; i++) {
    const prev = arr[i - 1];
    const curr = arr[i];

    const prevAuthorId = getAuthorId(prev);
    const currAuthorId = getAuthorId(curr);

    if (!prevAuthorId || !currAuthorId) continue;
    if (prevAuthorId !== currAuthorId) continue;

    let swapIndex = -1;

    for (let j = i + 1; j < arr.length; j++) {
      const candidate = arr[j];
      const candidateAuthorId = getAuthorId(candidate);

      if (candidateAuthorId && candidateAuthorId !== prevAuthorId) {
        swapIndex = j;
        break;
      }
    }

    if (swapIndex !== -1) {
      [arr[i], arr[swapIndex]] = [arr[swapIndex], arr[i]];
    }
  }

  return arr;
}

const SearchResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userId, isLoggedIn } = useAuth();

  // Mobile Home's search bar navigates here with `state: { autofocus: true }`
  // (a tap on its still-empty field, or a submit) so typing continues
  // straight into SearchResults' own main input instead of stalling there.
  // Read once into a ref (not state) so it fires at most one focus() per
  // mount — later rerenders (typing, tab switches, etc.) don't touch it.
  const promptInputRef = useRef(null);
  const autofocusOnMountRef = useRef(Boolean(location.state?.autofocus));

  // AI-hero greeting name — same `GET /api/account/:userId` call
  // Header.js/Sidebar.js already make for avatar/username; no new API
  // contract. Best-effort only: an anonymous/failed fetch just falls back
  // to the impersonal greeting, never blocks anything else on this page.
  const [greetingName, setGreetingName] = useState("");

  useEffect(() => {
    if (!userId) {
      setGreetingName("");
      return;
    }

    let alive = true;

    (async () => {
      try {
        const token = await getAccessToken().catch(() => null);

        const response = await fetch(`${API_BASE}/api/account/${userId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) return;

        const data = await response.json().catch(() => null);
        const name = data?.username || data?.firstName || "";

        if (alive) setGreetingName(name);
      } catch {
        // best-effort — impersonal greeting fallback stays
      }
    })();

    return () => {
      alive = false;
    };
  }, [userId]);

  const qs = new URLSearchParams(location.search);
  const queryFromURL = qs.get('q') || '';
  const tabFromURL = qs.get('tab') || 'all';
  const categoryFromURL = qs.get('category') || '';
  const sortFromURL = qs.get('sort') || 'new';
  const cityFromURL = qs.get('city') || '';
  const currencyFromURL = qs.get('currency') || '';
  const priceMinFromURL = qs.get('priceMin');
  const priceMaxFromURL = qs.get('priceMax');
  const freeFromURL = qs.get('free') === '1';
  const exchangeFromURL = qs.get('exchange') === '1';
  const wantedFromURL = qs.get('wanted') === '1';

  const conditionFromURL = qs.get('condition') || '';
  const negotiableFromURL = qs.get('negotiable') || '';

  const profileTypeFromURL = qs.get('profileType') || '';
  const genderFromURL = qs.get('gender') || '';
  const ageMinFromURL = qs.get('ageMin');
  const ageMaxFromURL = qs.get('ageMax');

  // Fashion-first shopping refinement (2026-09) — Home's Women/Men/Kids
  // tiles + department chips navigate here as plain URL params (no separate
  // local-only state — see ShoppingRefinementBar.js). Read the exact same
  // way every other URL-driven filter on this page already is.
  const audienceFromURL = qs.get('audience') || '';
  const departmentFromURL = qs.get('department') || '';
  const shoppingCategoryFromURL = qs.get('shoppingCategory') || '';
  const brandFromURL = qs.get('brand') || '';

  const [searchQuery, setSearchQuery] = useState(queryFromURL);
  // Ordinary Search Package 1 — `searchQuery` stays bound to the input for
  // instant typing feedback. The fetch-triggering effects/cache-keys below
  // use `queryFromURL` instead (not `searchQuery`) — it's a plain const
  // re-derived from `location.search` on every render, exactly like
  // `cacheKeyBase`/`adsCacheKey` already are, so it only changes in the same
  // render pass as those, with no separate state/effect of its own. That
  // matters: an earlier version of this introduced its own debounced state
  // updated via an effect one render behind `adsCacheKey`, which caused a
  // real extra fetch — `adsCacheKey` (synchronous with the URL) would change
  // on one render while the separate state (effect-driven) hadn't caught up
  // yet, firing the effect twice for a single settle. Deriving straight from
  // `location.search`, like the URL's other consumers already do, avoids
  // that by construction. The URL itself is only pushed immediately for an
  // explicit action (Enter) — for plain typing it's debounced in
  // handleSearchChange — which is what actually avoids firing a request pair
  // on every keystroke.
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    if (!autofocusOnMountRef.current) return;
    autofocusOnMountRef.current = false;
    promptInputRef.current?.focus({ preventScroll: true });
  }, []);

  const [activeTab, setActiveTab] = useState(tabFromURL);
  const [category, setCategory] = useState(categoryFromURL);
  const [sort, setSort] = useState(sortFromURL);
  const [audience, setAudience] = useState(audienceFromURL);
  const [department, setDepartment] = useState(departmentFromURL);
  const [shoppingCategory, setShoppingCategory] = useState(shoppingCategoryFromURL);
  const [brand, setBrand] = useState(brandFromURL);
  // Cvanto Search Phase 1.5 — Unified Search's Agent Mode (handoff §19/§20),
  // opened exclusively via SearchHero's orb, now backed by the real
  // conversational Cvanto AI core (CvantoAgentModal) instead of the old
  // one-shot /api/search-assistant flow. `aiEntryMode` decides whether it
  // opens straight into voice-listening or the text composer; the modal
  // owns everything else about the AI flow itself.
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiEntryMode, setAiEntryMode] = useState('text');

  // Unified Search State (handoff §25.5/§25.14/§26.3) — "conversation
  // controls search; SearchResults displays marketplace inventory". An
  // Agent turn's results REPLACE what the catalogue shows, as an overlay on
  // top of the existing manual `ads`/`filteredUsers` state — deliberately not
  // merged into it, because the two are different retrieval engines (§24.1's
  // explicit decision not to unify them this phase) and manual filters must
  // keep working against their own results underneath once this clears.
  // Cleared the moment the user does anything manual (`pushURL`, below, is
  // the one choke point every manual mutation already goes through) — that's
  // what makes "закриваю Agent, results залишаються" / "вручну змінюю
  // filter" both true from the §25.17 acceptance test.
  const [agentResultSet, setAgentResultSet] = useState(null);
  const [agentLoadingMore, setAgentLoadingMore] = useState(false);
  const agentLoadingMoreRef = useRef(false);
  const agentLoadMoreRef = useRef(null);

  // Next page of the Agent's own result set (same intent => same hard filters and total the Agent reported).
  const loadMoreAgentResults = useCallback(async () => {
    const current = agentResultSet;
    if (!current || !current.hasMore || !current.intent || agentLoadingMoreRef.current) return;

    agentLoadingMoreRef.current = true;
    setAgentLoadingMore(true);
    const intentAtStart = current.intent;

    try {
      const page = await agentSearchPage(intentAtStart, current.listings.length);
      setAgentResultSet((prev) => {
        // a stale response for a result set that has since been replaced is dropped
        if (!prev || prev.intent !== intentAtStart) return prev;
        const keyOf = (l) => String(l?.listingId || l?.id || '');
        const seen = new Set(prev.listings.map(keyOf));
        const fresh = (page.listings || []).filter((l) => !seen.has(keyOf(l)));
        return { ...prev, listings: [...prev.listings, ...fresh], total: page.total, hasMore: page.hasMore === true && fresh.length > 0 };
      });
    } catch {
      /* the next scroll retries; what is on screen stays valid */
    } finally {
      agentLoadingMoreRef.current = false;
      setAgentLoadingMore(false);
    }
  }, [agentResultSet]);

  useEffect(() => {
    if (!agentResultSet?.hasMore) return undefined;
    const node = agentLoadMoreRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreAgentResults();
      },
      { root: null, rootMargin: '600px 0px', threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [agentResultSet, loadMoreAgentResults]);

  const handleOpenAI = (mode) => {
    setAiEntryMode(mode === 'voice' ? 'voice' : 'text');
    setAiModalOpen(true);
  };

  const handleCloseAI = () => setAiModalOpen(false);

  const handleAgentResults = (results) => {
    const incoming = Array.isArray(results?.listings) ? results.listings : [];
    const universe = typeof results?.universe === 'string' ? results.universe : null;
    const offset = typeof results?.offset === 'number' ? results.offset : 0;
    const keyOf = (l) => String(l?.listingId || l?.id || '');

    agentLoadingMoreRef.current = false;
    setAgentLoadingMore(false);
    setAgentResultSet((prev) => {
      const total = typeof results?.total === 'number' ? results.total : incoming.length;
      // §44: "покажи наступні" is the NEXT PAGE of the set already on screen (same universe, offset > 0): append it.
      if (offset > 0 && prev && universe && prev.universe === universe) {
        const seen = new Set(prev.listings.map(keyOf));
        return {
          ...prev,
          listings: [...prev.listings, ...incoming.filter((l) => !seen.has(keyOf(l)))],
          total,
          hasMore: results?.hasMore === true,
          intent: results?.intent ?? prev.intent,
        };
      }
      // A next page with nothing to append to (e.g. restored from history): rebuild the set from its first page.
      if (offset > 0 && results?.intent) {
        const intentAtStart = results.intent;
        agentSearchPage(intentAtStart, 0)
          .then((page) =>
            setAgentResultSet({
              query: results?.query || '',
              listings: page.listings || [],
              people: [],
              total: page.total,
              hasMore: page.hasMore === true,
              intent: intentAtStart,
              universe,
            })
          )
          .catch(() => {});
        return prev;
      }
      return {
        query: results?.query || '',
        listings: incoming,
        people: Array.isArray(results?.people) ? results.people : [],
        // §36: the REAL number of matches, whether more pages exist and the canonical intent that pages the SAME set
        total,
        hasMore: results?.hasMore === true,
        intent: results?.intent ?? null,
        universe,
      };
    });
    // "Все" is the central workspace (§25.2) — an agent turn can return both
    // listings and people, so it always lands there, regardless of whichever
    // tab was active before opening the Agent.
    setActiveTab('all');
  };

  const tabOrder = useMemo(() => ['all', 'ads', 'users'], []);
  const [prevTab, setPrevTab] = useState(tabFromURL);
  const [slideDir, setSlideDir] = useState('left');

  // "Все" tab infinite-continuation — reaching the bottom of the (now
  // 20-item) listings preview silently promotes the SAME mounted panel
  // into the full "Оголошення" pagination experience: `activeTab` becomes
  // 'ads' (so the segmented pill/URL reflect reality) but the "all"-tab
  // JSX branch below keeps rendering (see its condition) instead of
  // unmounting in favor of the separate dedicated-tab branch — that's what
  // makes this invisible: no DOM swap, no scroll jump, nothing above the
  // fold changes. `listingsExpanded` is that "stay on the all-panel, just
  // in infinite mode now" flag.
  const [listingsExpanded, setListingsExpanded] = useState(false);
  const allTabBottomSentinelRef = useRef(null);

  // Sibling one-shot flags consumed by the URL-sync effect and the
  // ads-reset-fetch effect respectively — both exist purely so the ONE
  // silent auto-continue transition (below) can update `activeTab`/the URL
  // without (a) replaying the tab-switch slide animation or (b) discarding
  // the already-loaded preview items with a fresh reset fetch. Any other,
  // real tab/query change goes through those effects completely normally.
  const silentTabSyncRef = useRef(false);
  const skipNextAdsResetRef = useRef(false);

    const [general, setGeneral] = useState({
    city: cityFromURL,
    currency: currencyFromURL,
    priceMin: priceMinFromURL ? Number(priceMinFromURL) : '',
    priceMax: priceMaxFromURL ? Number(priceMaxFromURL) : '',
    sort: sortFromURL,
    free: freeFromURL,
    exchange: exchangeFromURL,
    wanted: wantedFromURL,

    condition: conditionFromURL,
    negotiable: negotiableFromURL,

    profileType: profileTypeFromURL,
    gender: genderFromURL,
    ageMin: ageMinFromURL ? Number(ageMinFromURL) : '',
    ageMax: ageMaxFromURL ? Number(ageMaxFromURL) : '',
  });

  // The current manual search state, handed to the Agent only to SEED a
  // brand-new conversation (handoff §25.7 point 1: "Agent gets the current
  // search context" instead of starting independent). Intentionally NOT
  // reactive to every keystroke — CvantoAgentModal reads it once per open via
  // useCvantoChat's ref, and each open already starts a fresh conversation.
  const agentSearchContext = queryFromURL.trim()
    ? {
        query: queryFromURL.trim(),
        city: general.city || null,
        minPrice: general.priceMin !== '' && general.priceMin != null ? general.priceMin : null,
        maxPrice: general.priceMax !== '' && general.priceMax != null ? general.priceMax : null,
        currency: general.currency || null,
      }
    : null;

  const [filteredUsers, setFilteredUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');

  const [usersNextCursor, setUsersNextCursor] = useState(null);
  const [usersHasMore, setUsersHasMore] = useState(false);

  const usersAbortRef = useRef(null);
  const usersRequestIdRef = useRef(0);
  const usersLoadMoreRef = useRef(null);

  const adsLoadMoreRef = useRef(null);
  const lastTrackedSearchRef = useRef("");

  const [adsReachedEnd, setAdsReachedEnd] = useState(false);
  const [ads, setAds] = useState([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [adsError, setAdsError] = useState('');
  const [adsNextCursor, setAdsNextCursor] = useState(null);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [attrFilters, setAttrFilters] = useState({});

  const [subscribed, setSubscribed] = useState(false);
  const [subBusy, setSubBusy] = useState(false);
  const [subErr, setSubErr] = useState('');

  const [hideHeader, setHideHeader] = useState(false);
  useEffect(() => {
    let lastY = window.scrollY || 0;
    const onScroll = () => {
      const y = window.scrollY || 0;
      const scrollingDown = y > lastY;
      if (scrollingDown && y > 80) setHideHeader(true);
      else if (!scrollingDown) setHideHeader(false);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const [subAnim, setSubAnim] = useState('');
  useEffect(() => {
    if (!subAnim) return;
    const id = setTimeout(() => setSubAnim(''), 800);
    return () => clearTimeout(id);
  }, [subAnim]);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const nextTab = p.get('tab') || 'all';

    // The one silent auto-continue transition sets this right before its
    // own `pushURL({ tab: 'ads' })` — skip the slide-direction bookkeeping
    // for exactly that one sync so the still-mounted "all" panel never
    // gets a new `animation-name` (see .slide-left/.slide-right .tab-panel
    // in SearchResults.scss) and therefore never replays its entrance
    // animation. Every real, user-initiated tab change leaves this false
    // and behaves exactly as before.
    if (silentTabSyncRef.current) {
      silentTabSyncRef.current = false;
    } else {
      const prevIdx = tabOrder.indexOf(activeTab);
      const nextIdx = tabOrder.indexOf(nextTab);
      if (prevIdx !== -1 && nextIdx !== -1) {
        setSlideDir(nextIdx > prevIdx ? 'left' : 'right');
        setPrevTab(activeTab);
      }
    }

    setSearchQuery(p.get('q') || '');
    setActiveTab(nextTab);
    setCategory(p.get('category') || '');
    setSort(p.get('sort') || 'new');
    setAudience(p.get('audience') || '');
    setDepartment(p.get('department') || '');
    setShoppingCategory(p.get('shoppingCategory') || '');
    setBrand(p.get('brand') || '');

    const city = p.get('city') || '';
    const currency = p.get('currency') || '';
    const priceMin = p.get('priceMin');
    const priceMax = p.get('priceMax');
    const free = p.get('free') === '1';
    const exchange = p.get('exchange') === '1';
    const wanted = p.get('wanted') === '1';

    const condition = p.get('condition') || '';
    const negotiable = p.get('negotiable') || '';

    const profileType = p.get('profileType') || '';
    const gender = p.get('gender') || '';
    const ageMin = p.get('ageMin');
    const ageMax = p.get('ageMax');

      setGeneral({
      city,
      currency,
      priceMin: priceMin ? Number(priceMin) : '',
      priceMax: priceMax ? Number(priceMax) : '',
      sort: p.get('sort') || 'new',
      free,
      exchange,
      wanted,

      condition,
      negotiable,

      profileType,
      gender,
      ageMin: ageMin ? Number(ageMin) : '',
      ageMax: ageMax ? Number(ageMax) : '',
    });

    setAttrFilters(parseAttrParamsFromSearch(p));
  }, [location.search, activeTab, tabOrder]);

  const adsAbortRef = useRef(null);
  const adsRequestIdRef = useRef(0);

useEffect(() => {
  const cleanQuery = String(searchQuery || "").trim();

  if (cleanQuery.length < 2) return;

  const trackKey = [
    cleanQuery.toLowerCase(),
    activeTab || "all",
    category || "",
    general?.city || "",
  ].join("|");

  const timeoutId = setTimeout(() => {
    if (lastTrackedSearchRef.current === trackKey) return;

    lastTrackedSearchRef.current = trackKey;

    trackSearchQuery({
      query: cleanQuery,
      tab: activeTab || "all",
      category: category || null,
      city: general?.city || null,
    });
  }, 1200);

  return () => clearTimeout(timeoutId);
}, [searchQuery, activeTab, category, general?.city]);

  const filtersActive = useMemo(() => {
    if (category) return true;
    const g = general || {};
    if (g.city || g.currency || g.free || g.exchange || g.wanted) return true;

    if (g.condition || g.negotiable) return true;

    if (g.profileType || g.gender) return true;
    if (g.ageMin !== '' && g.ageMin != null) return true;
    if (g.ageMax !== '' && g.ageMax != null) return true;

    if (g.priceMin !== '' && g.priceMin != null) return true;
    if (g.priceMax !== '' && g.priceMax != null) return true;
    if (sort && sort !== 'new') return true;
    if (attrFilters && Object.keys(attrFilters).length > 0) {
      return Object.values(attrFilters).some(f =>
        (f.type === 'number' && ((f.min !== '' && f.min != null) || (f.max !== '' && f.max != null))) ||
        (f.type !== 'number' && (f.value ?? '') !== '')
      );
    }
    return false;
  }, [category, general, sort, attrFilters]);

  const cacheKeyBase = useMemo(() => {
    const p = new URLSearchParams(location.search || '');
    return p.toString();
  }, [location.search]);

  const usersCacheKey = useMemo(
    () => `sr:users:${userId || 'anon'}:${activeTab}:${cacheKeyBase}`,
    [userId, activeTab, cacheKeyBase]
  );

  const adsCacheKey = useMemo(
    () => `sr:ads:${activeTab}:${cacheKeyBase}`,
    [activeTab, cacheKeyBase]
  );

  const usersTTL = useMemo(() => {
    return computeSmartTTL({
      kind: 'users',
      tab: activeTab,
      q: queryFromURL,
      category,
      sort: sort || (general?.sort || 'new'),
      filtersActive,
      hasAnyUserContext: !!(userId || isLoggedIn),
    });
  }, [activeTab, queryFromURL, category, sort, general?.sort, filtersActive, userId, isLoggedIn]);

  const adsTTL = useMemo(() => {
  return computeSmartTTL({
    kind: 'ads',
    tab: activeTab,
    q: queryFromURL,
    category,
    sort: sort || (general?.sort || 'new'),
    filtersActive,
    hasAnyUserContext: !!(userId || isLoggedIn),
  });
}, [activeTab, queryFromURL, category, sort, general?.sort, filtersActive, userId, isLoggedIn]);

// Unified Search display layer (§25.5): when an Agent turn's results are
// active they REPLACE what's shown, without touching the manual `ads`/
// `filteredUsers` state or fetch machinery underneath at all (see the
// `agentResultSet` state comment above).
const effectiveAds = agentResultSet ? agentResultSet.listings : ads;
const effectiveUsers = agentResultSet ? agentResultSet.people : filteredUsers;
const effectiveAdsLoading = agentResultSet ? false : adsLoading;
const effectiveAdsError = agentResultSet ? '' : adsError;
const effectiveUsersLoading = agentResultSet ? false : usersLoading;
const effectiveUsersError = agentResultSet ? '' : usersError;

const renderAds = useMemo(() => {
  return spreadSameAuthorAds(effectiveAds);
}, [effectiveAds]);

// Fixed insertion position for the search_results_inline placement — an
// extra grid cell rendered alongside organic results, same idea as Feed's
// adInsertAfter. Never touches `ads`/`renderAds`/adsNextCursor, so organic
// pagination/cursor is unaffected either way.
const SEARCH_AD_INSERT_AFTER_IDX = 7;

  const fetchUsers = async ({ append = false, cursor = null } = {}) => {
  const requestId = ++usersRequestIdRef.current;

  if (usersAbortRef.current) {
    usersAbortRef.current.abort();
  }

  const controller = new AbortController();
  usersAbortRef.current = controller;

  try {
    if (!append) {
      setFilteredUsers([]);
      setUsersNextCursor(null);
      setUsersHasMore(false);
    }

    setUsersError('');
    setUsersLoading(true);

    const params = new URLSearchParams();
    // Polish pass: "all" tab preview now shows up to 12 avatars (was 5) to
    // match the dedicated "Люди" tab's own page size — same endpoint, same
    // params shape, just a larger limit value so the preview row has
    // enough real items to actually scroll.
    const usersLimit = '12';

    params.set('limit', usersLimit);
    if (userId) params.set('userId', String(userId).trim());
    if (queryFromURL.trim()) params.set('q', queryFromURL.trim());
    if (cursor) params.set('cursor', cursor);

    if (general?.city) {
      const cityQ = getLocationQueryForAPI(String(general.city).trim());
      if (cityQ) params.set('city', cityQ);
    }

    if (general?.profileType) params.set('profileType', String(general.profileType).trim());
    if (general?.gender) params.set('gender', String(general.gender).trim());
    if (general?.ageMin !== '' && general?.ageMin != null) params.set('ageMin', String(general.ageMin));
    if (general?.ageMax !== '' && general?.ageMax != null) params.set('ageMax', String(general.ageMax));

    const url = `${API_BASE}/api/account/search?${params.toString()}`;
    const response = await fetch(url, { signal: controller.signal });

    if (!response.ok) {
      const t = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status} ${t || ''}`.trim());
    }

    const data = await response.json();

    if (controller.signal.aborted) return;
    if (requestId !== usersRequestIdRef.current) return;

    const itemsRaw = Array.isArray(data)
      ? data
      : Array.isArray(data.items)
        ? data.items
        : [];

    const arr = general?.city
      ? itemsRaw.filter((u) => locationSoftMatch(u?.location || u?.city || u?.profileCity || "", general.city))
      : itemsRaw;

    setFilteredUsers((prev) => {
      if (!append) return arr;

      const seen = new Set(prev.map((u) => String(u.id)));
      const merged = [...prev];

      for (const u of arr) {
        if (!seen.has(String(u.id))) {
          seen.add(String(u.id));
          merged.push(u);
        }
      }

      return merged;
    });

    const nextCursor = Array.isArray(data) ? null : (data.nextCursor || null);
    setUsersNextCursor(nextCursor);
    setUsersHasMore(!!nextCursor);
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('❌ Помилка завантаження користувачів:', error);
      setUsersError('Не вдалося завантажити людей');
      if (!append) {
        setFilteredUsers([]);
        setUsersNextCursor(null);
        setUsersHasMore(false);
      }
    }
  } finally {
    if (requestId === usersRequestIdRef.current) {
      setUsersLoading(false);
    }
  }
};

useEffect(() => {
  if (!(activeTab === 'users' || activeTab === 'all')) return;

  fetchUsers({ append: false, cursor: null });
}, [queryFromURL, activeTab, userId, JSON.stringify(general)]);

    // ADS
  const fetchAds = async ({ append = false, limit } = {}) => {
  const requestId = ++adsRequestIdRef.current;
    const allowAdsCache = String(sort || 'new') !== 'new';

    if (adsAbortRef.current) {
      adsAbortRef.current.abort();
    }

    const controller = new AbortController();
    adsAbortRef.current = controller;

    try {
      if (!append) {
        setAdsReachedEnd(false);

        if (allowAdsCache) {
          const cached = safeLoadCache(adsCacheKey);

          if (cached && Array.isArray(cached.items)) {
            if (requestId !== adsRequestIdRef.current) return;

            setAdsError('');
            setAds(Array.isArray(cached.items) ? cached.items : []);
            setAdsNextCursor(cached.nextCursor || null);
            setAdsReachedEnd(!cached.nextCursor);
            setAdsLoading(false);
            return;
          }
        }

        setAds([]);
        setAdsNextCursor(null);
      }

      setAdsLoading(true);
      setAdsError('');

      const params = new URLSearchParams();
      params.set('type', 'listings');
      params.set('sort', sort || 'new');

      if (limit) params.set('limit', String(limit));
      if (queryFromURL.trim()) params.set('q', queryFromURL.trim());
      if (category) params.set('category', category);

      if (audience) params.set('audience', audience);
      if (department) params.set('department', department);
      if (shoppingCategory) params.set('shoppingCategory', shoppingCategory);
      if (brand) params.set('brand', brand);

      if (general.city) {
        const cityQ = getLocationQueryForAPI(general.city);
        if (cityQ) params.set('city', cityQ);
      }

      if (general.currency) params.set('currency', general.currency);
      if (general.priceMin !== '' && general.priceMin != null) params.set('priceMin', String(general.priceMin));
      if (general.priceMax !== '' && general.priceMax != null) params.set('priceMax', String(general.priceMax));
      if (general.free) params.set('free', '1');
      if (general.exchange) params.set('exchange', '1');
      if (general.wanted) params.set('wanted', '1');

      if (general.condition) params.set('condition', general.condition);
      if (general.negotiable) params.set('negotiable', general.negotiable);

      if (append && adsNextCursor) params.set('cursor', adsNextCursor);

      buildAttrURLEntries(attrFilters).forEach(([key, value]) => params.append(key, value));

      const resp = await fetch(`${API_BASE}/api/search/feed?${params.toString()}`, {
        signal: controller.signal,
      });

      if (!resp.ok) throw new Error('Search feed error');

      const data = await resp.json();

      if (controller.signal.aborted) return;
      if (requestId !== adsRequestIdRef.current) return;

      const rawItems = Array.isArray(data.items) ? data.items : [];

      const newItems = general?.city
        ? rawItems.filter((it) => locationSoftMatch(it?.location || "", general.city))
        : rawItems;

      const nextCursor = data.nextCursor || null;

      let addedUniqueCount = 0;

      setAds((prev) => {
        if (requestId !== adsRequestIdRef.current) return prev;

        const prevIds = new Set(
          Array.isArray(prev)
            ? prev.map((it) => String(it?.listingId || it?.id || '')).filter(Boolean)
            : []
        );

        const mergedRaw = append ? [...prev, ...newItems] : newItems;

        const mergedFiltered = general?.city
          ? mergedRaw.filter((it) => locationSoftMatch(it?.location || "", general.city))
          : mergedRaw;

        const seen = new Set();
        const merged = [];

        for (const item of mergedFiltered) {
          const id = String(item?.listingId || item?.id || '');
          if (!id) continue;
          if (seen.has(id)) continue;

          seen.add(id);
          merged.push(item);

          if (append && !prevIds.has(id)) {
            addedUniqueCount += 1;
          }
        }

        if (allowAdsCache) {
          safeSaveCache(
            adsCacheKey,
            {
              items: merged,
              nextCursor,
            },
            adsTTL
          );
        }

        return merged;
      });

      if (!nextCursor || (append && addedUniqueCount === 0)) {
        setAdsNextCursor(null);
        setAdsReachedEnd(true);
      } else {
        setAdsNextCursor(nextCursor);
        setAdsReachedEnd(false);
      }
    } catch (e) {
      if (e.name === 'AbortError') return;

      console.error('❌ Помилка завантаження оголошень:', e);

      if (requestId !== adsRequestIdRef.current) return;

      setAdsError('Сталася помилка під час завантаження.');
      if (!append) setAds([]);
      setAdsNextCursor(null);
      setAdsReachedEnd(false);
    } finally {
      if (requestId === adsRequestIdRef.current) {
        setAdsLoading(false);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (adsAbortRef.current) {
        adsAbortRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (usersAbortRef.current) {
        usersAbortRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

useEffect(() => {
  if (activeTab !== 'ads') return;
  if (!adsNextCursor) return;
  if (adsLoading) return;

  const node = adsLoadMoreRef.current;
  if (!node) return;

  const observer = new IntersectionObserver(
    (entries) => {
      const first = entries[0];
      if (first && first.isIntersecting && adsNextCursor && !adsLoading) {
        fetchAds({ append: true });
      }
    },
    {
      root: null,
      rootMargin: '300px 0px',
      threshold: 0.01,
    }
  );

  observer.observe(node);

  return () => observer.disconnect();
}, [activeTab, adsNextCursor, adsLoading]);

// "Все" tab, capped preview (not yet expanded): reaching the bottom of the
// visible ~20 items silently promotes into the infinite "Оголошення"
// pagination above (same `ads`/`adsNextCursor` state, same
// `adsLoadMoreRef` sentinel/effect — just now reachable because
// `activeTab` becomes 'ads'). Only arms when there's demonstrably more to
// continue into (`adsNextCursor` truthy) — with ≤20 total results there is
// nothing to auto-continue, so this never fires.
useEffect(() => {
  if (activeTab !== 'all') return;
  if (listingsExpanded) return;
  if (!adsNextCursor) return;
  if (adsLoading) return;
  // Agent results are a fixed, one-shot list (no cursor of their own) shown
  // OVER the manual `ads` state — the manual pagination cursor underneath is
  // irrelevant to what's actually on screen right now, so this must not
  // silently promote/clear the agent overlay just because the user scrolled
  // it (see the `agentResultSet` comment above).
  if (agentResultSet) return;

  const node = allTabBottomSentinelRef.current;
  if (!node) return;

  const observer = new IntersectionObserver(
    (entries) => {
      const first = entries[0];
      if (first && first.isIntersecting && adsNextCursor && !adsLoading) {
        silentTabSyncRef.current = true;
        skipNextAdsResetRef.current = true;
        setListingsExpanded(true);
        pushURL({ tab: 'ads' });
      }
    },
    {
      root: null,
      rootMargin: '400px 0px',
      threshold: 0.01,
    }
  );

  observer.observe(node);

  return () => observer.disconnect();
  // `pushURL` intentionally omitted — recreated every render, same convention
  // already used by this file's other effects (e.g. the ads-reset effect
  // below). `agentResultSet` IS included: unlike a function, its identity
  // only changes on a real state update, and the guard above must see it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [activeTab, listingsExpanded, adsNextCursor, adsLoading, agentResultSet]);

useEffect(() => {
  if (activeTab !== 'users') return;
  if (!usersHasMore) return;
  if (usersLoading) return;

  const node = usersLoadMoreRef.current;
  if (!node) return;

  const observer = new IntersectionObserver(
    (entries) => {
      const first = entries[0];
      if (first && first.isIntersecting && usersNextCursor && !usersLoading) {
        fetchUsers({ append: true, cursor: usersNextCursor });
      }
    },
    {
      root: null,
      rootMargin: '300px 0px',
      threshold: 0.01,
    }
  );

  observer.observe(node);

  return () => observer.disconnect();
}, [activeTab, usersHasMore, usersNextCursor, usersLoading]);

  useEffect(() => {
    // The one silent auto-continue transition (bottom of the "Все"
    // preview -> "Оголошення") flips `activeTab` to 'ads' purely for the
    // pill/URL — it must NOT reset `ads` back to an empty array and
    // refetch, or the whole point (seamless, already-loaded continuation)
    // is defeated. `pushURL`'s navigate() lands `activeTab: 'ads'` a render
    // or two AFTER this effect first re-fires (still seeing the old 'all'),
    // so this same transition trips this effect MORE THAN ONCE — the flag
    // stays armed (skipping every one of those runs) until it actually
    // observes the settled `activeTab === 'ads'`, only then consuming it.
    if (skipNextAdsResetRef.current) {
      if (activeTab === 'ads') {
        skipNextAdsResetRef.current = false;
      }
      return;
    }

    // Any other reason this effect re-runs (a real tab click, a new query,
    // a filter change, ...) is a genuine reset — also drop back out of
    // "expanded" mode, since it no longer describes a continuation of
    // anything that was actually loaded.
    setListingsExpanded(false);

    if (activeTab === 'ads' || activeTab === 'all') {
      // Preview ("Все") now shows up to 20 (was 16/10) — same endpoint,
      // just a larger `limit` value, same as the earlier People bump.
      const limitForAll = activeTab === 'all' ? 20 : undefined;
      fetchAds({ append: false, limit: limitForAll });
    }
  }, [activeTab, queryFromURL, category, sort, audience, department, shoppingCategory, brand, JSON.stringify(general), JSON.stringify(attrFilters), adsCacheKey, adsTTL]);

  useEffect(() => {
  let cancelled = false;

  (async () => {
    setSubErr('');

    if (!isLoggedIn || !userId || !category) {
      setSubscribed(false);
      return;
    }

    try {
      const token = await getAccessToken();

      if (!token) {
        if (!cancelled) setSubscribed(false);
        return;
      }

      const q = new URLSearchParams({ userId, category });

      const r = await fetch(`${API_BASE}/api/category-subs/status?` + q.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!r.ok) {
        if (!cancelled) setSubscribed(false);
        return;
      }

      const j = await r.json();

      if (!cancelled) setSubscribed(!!j.subscribed);
    } catch (e) {
      if (!cancelled) setSubErr('Не вдалося отримати статус підписки');
    }
  })();

  return () => {
    cancelled = true;
  };
}, [isLoggedIn, userId, category]);

  const toggleCategorySub = async () => {
    if (!isLoggedIn || !userId) {
      navigate('/login');
      return;
    }
    if (!category) return;
    setSubBusy(true);
    setSubErr('');
    try {
      const url = subscribed
        ? `${API_BASE}/api/category-subs/unsubscribe`
        : `${API_BASE}/api/category-subs/subscribe`;
      const token = await getAccessToken();

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ userId, category }),
      });
      const j = await res.json();
      if (!res.ok || j.error) throw new Error(j.error || 'fail');
      const next = !subscribed;
      setSubscribed(next);
      setSubAnim(next ? 'on' : 'off');
    } catch (e) {
      setSubErr('Операція не вдалася');
    } finally {
      setSubBusy(false);
    }
  };

  const pushURL = (next) => {
    // Single choke point every manual mutation already goes through (typing,
    // Enter, tab switch, filter apply/reset, shopping-refinement chips) — see
    // the `agentResultSet` comment above for why clearing it here is enough.
    setAgentResultSet(null);

    const p = new URLSearchParams(location.search);
    Object.entries(next).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') p.delete(k);
      else p.set(k, v);
    });
    navigate(`/search?${p.toString()}`);
  };

const openProfileTracked = (targetUserId) => {
  const id = String(targetUserId || "").trim();

  if (!id) return;

  trackOpenProfile({
    targetUserId: id,
  });

  navigate(`/profile/${id}`);
};

const openListingTracked = (item) => {
  const listingId = String(item?.listingId || item?.id || "").trim();

  if (!listingId) return;

  trackOpenListingDetails({
    listingId,
    category: item?.category || null,
  });

  navigate(`/listing/${listingId}`);
};

  const SEARCH_TYPING_DEBOUNCE_MS = 280;

  const handleSearchChange = (value) => {
  setSearchQuery(value);

  // Package 1 — implicit typing: delay the URL push (and therefore
  // `queryFromURL`/`adsCacheKey`, which are re-derived from `location.search`
  // on every render) instead of firing it on every keystroke. The input
  // itself already updated above, so typing still feels instant.
  if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  searchDebounceRef.current = setTimeout(() => {
    searchDebounceRef.current = null;
    pushURL({
      q: value,
      cvantoSearch: null,
    });
  }, SEARCH_TYPING_DEBOUNCE_MS);
};

const handleKeyPress = (e) => {
  if (e.key !== "Enter") return;

  e.preventDefault();

  // Package 1 — explicit action: cancel any pending debounced push and
  // search immediately, bypassing the typing delay entirely.
  if (searchDebounceRef.current) {
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = null;
  }

  pushURL({ q: searchQuery });
};

  const handleTabChange = (tab) => {
    const prevIdx = tabOrder.indexOf(activeTab);
    const nextIdx = tabOrder.indexOf(tab);
    setSlideDir(nextIdx > prevIdx ? 'left' : 'right');
    setPrevTab(activeTab);
    setActiveTab(tab);
    // Any deliberate tap on a tab pill exits the silent auto-continue mode
    // — a manual switch should always land on that tab's normal/default
    // view, not stay stuck showing the lighter preview-tile infinite list.
    setListingsExpanded(false);
    pushURL({ tab });
  };
  const handleOpenFilters = () => setIsFilterOpen(true);
  const handleCloseFilters = () => setIsFilterOpen(false);

  const resetFilters = () => {
  const p = new URLSearchParams(location.search);

  [
    'category',
    'city',
    'currency',
    'priceMin',
    'priceMax',
    'sort',
    'free',
    'exchange',
    'wanted',
    'condition',
    'negotiable',
    'profileType',
    'gender',
    'ageMin',
    'ageMax'
  ].forEach((k) => p.delete(k));

  Array.from(p.keys()).forEach((k) => {
    if (k.startsWith('attr.')) p.delete(k);
  });

  p.set('tab', 'ads');
  navigate(`/search?${p.toString()}`);
};

  const applyFromModalToURL = (preparedAttrs, preparedGeneral) => {
    const p = new URLSearchParams(location.search);

    if (preparedGeneral.category) p.set('category', preparedGeneral.category);
    else p.delete('category');

    preparedGeneral.city ? p.set('city', preparedGeneral.city) : p.delete('city');
    preparedGeneral.currency ? p.set('currency', preparedGeneral.currency) : p.delete('currency');

    if (preparedGeneral.priceMin != null && preparedGeneral.priceMin !== '') p.set('priceMin', String(preparedGeneral.priceMin));
    else p.delete('priceMin');
    if (preparedGeneral.priceMax != null && preparedGeneral.priceMax !== '') p.set('priceMax', String(preparedGeneral.priceMax));
    else p.delete('priceMax');

    p.set('sort', preparedGeneral.sort || 'new');
    preparedGeneral.free ? p.set('free', '1') : p.delete('free');
    preparedGeneral.exchange ? p.set('exchange', '1') : p.delete('exchange');
    preparedGeneral.wanted ? p.set('wanted', '1') : p.delete('wanted');

    preparedGeneral.condition ? p.set('condition', String(preparedGeneral.condition)) : p.delete('condition');
    preparedGeneral.negotiable ? p.set('negotiable', String(preparedGeneral.negotiable)) : p.delete('negotiable');

    preparedGeneral.profileType ? p.set('profileType', String(preparedGeneral.profileType)) : p.delete('profileType');
    preparedGeneral.gender ? p.set('gender', String(preparedGeneral.gender)) : p.delete('gender');
    if (preparedGeneral.ageMin != null && preparedGeneral.ageMin !== '') p.set('ageMin', String(preparedGeneral.ageMin));
    else p.delete('ageMin');
    if (preparedGeneral.ageMax != null && preparedGeneral.ageMax !== '') p.set('ageMax', String(preparedGeneral.ageMax));
    else p.delete('ageMax');

    Array.from(p.keys()).forEach((k) => { if (k.startsWith('attr.')) p.delete(k); });
    buildAttrURLEntries(preparedAttrs).forEach(([key, value]) => p.append(key, value));

    p.set('tab', 'ads');
    navigate(`/search?${p.toString()}`);
  };

  const renderUserSkeletons = (n = 6) => (
    <div className="user-results">
      {Array.from({ length: n }).map((_, i) => (
        <div className="user-skel-row shimmer" key={`usr-skel-${i}`}>
          <div className="circle" />
          <div className="lines">
            <div className="line w60" />
            <div className="line w40" />
          </div>
        </div>
      ))}
    </div>
  );

  const renderAdsSkeletons = (n = 8) => (
    <div className="ads-grid">
      {Array.from({ length: n }).map((_, i) => (
        <div className="ad-skel-card shimmer" key={`ad-skel-${i}`}>
          <div className="thumb" />
          <div className="meta">
            <div className="line w70" />
            <div className="line w40" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>

    <Helmet>
  {/* title */}
  <title>
    {(() => {
      const q = String(searchQuery || "").trim();
      const c = String(category || "").trim();
      const city = String(general?.city || "").trim();
      const tab = String(activeTab || "ads");

      const parts = [];
      if (q) parts.push(q);
      if (c) parts.push(c);
      if (city) parts.push(city);

      const base = parts.length ? parts.join(" — ") : "Пошук";
      const suffix = tab === "users" ? "людей" : "оголошень";
      return `${base} — пошук ${suffix} | Cvanto`;
    })()}
  </title>

  {/* description */}
  <meta
    name="description"
    content={(() => {
      const q = String(searchQuery || "").trim();
      const c = String(category || "").trim();
      const city = String(general?.city || "").trim();

      const bits = [];
      if (q) bits.push(`Запит: «${q}»`);
      if (c) bits.push(`Категорія: ${c}`);
      if (city) bits.push(`Місто: ${city}`);

      const head = bits.length ? bits.join(". ") + "." : "Пошук оголошень та людей у Cvanto.";
      return `${head} Фільтруйте за ціною, містом і категоріями — знаходьте швидко.`;
    })()}
  />

  {}
  <meta name="robots" content="noindex,follow" />

  <link
    rel="canonical"
    href={resolveSearchCanonical({
      origin: window.location.origin,
      category,
      nameToSlug,
      searchQuery,
      city: general?.city,
      priceMin: general?.priceMin,
      priceMax: general?.priceMax,
      attrFilters,
    })}
  />
</Helmet>

      <DynamicHeader
        variant="glass"
        size="compact"
        hideOnScroll={true}
        showBack={true}
        withSpacer={true}
        respectSafeAreaTop={true}
        centerNode={<SearchModeHeader activeTab={activeTab} onChange={handleTabChange} />}
        rightNode={
          <button
            type="button"
            className="search-filter-btn"
            onClick={handleOpenFilters}
            aria-label="Фільтри"
            title="Фільтри"
          >
            <img src={filterIcon} alt="Фільтри" />
            {filtersActive && <span className="search-filter-indicator" aria-hidden="true" />}
          </button>
        }
      />

      <div className={`search-results tab-${activeTab} ${aiModalOpen ? 'search-results--agent-open' : ''}`}>
        <div className="search-results__container">
          <div className={`search-sticky glass-frost fade-in ${hideHeader ? 'is-hidden' : ''}`}>
            <SearchHero
              inputRef={promptInputRef}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              onSearchKeyDown={handleKeyPress}
              category={category}
              subscribed={subscribed}
              subBusy={subBusy}
              subAnim={subAnim}
              onToggleSub={toggleCategorySub}
              username={greetingName}
              activeTab={activeTab}
              onOpenAI={handleOpenAI}
            />
          </div>

          <CvantoAgentModal
            open={aiModalOpen}
            entryMode={aiEntryMode}
            onClose={handleCloseAI}
            searchContext={agentSearchContext}
            onResults={handleAgentResults}
          />

          {activeTab !== 'users' && (
            <ShoppingRefinementBar
              audience={audience}
              department={department}
              shoppingCategory={shoppingCategory}
              onChange={pushURL}
            />
          )}

          <div className={`tabs-stage slide-${slideDir}`}>
            {activeTab === 'users' && (
            <div className="tab-panel">
              <div className="user-results list">
                {effectiveUsersLoading && effectiveUsers.length === 0 && renderUserSkeletons(8)}
                {effectiveUsersError && <div className="error">{effectiveUsersError}</div>}

                {effectiveUsers.length > 0 ? (
                  <>
                    {effectiveUsers.map((user, idx) => (
                      <div
                        key={user.id}
                        className="user-result-item list-row pop-in"
                        style={{ animationDelay: `${0.015 * idx}s` }}
                        onClick={() => openProfileTracked(user.id)}
                      >
                        <img src={user.avatar} alt={user.username} className="user-avatar" />
                        <div className="user-info">
                          <div className="user-username">{user.username}</div>
                          <div className="user-bio">{user.bio}</div>
                          <div className="user-type">{user.profileType}</div>
                        </div>
                      </div>
                    ))}

                    {!agentResultSet && usersHasMore && (
                      <div ref={usersLoadMoreRef} className="load-more-sentinel" aria-hidden="true">
                        {usersLoading ? 'Завантаження…' : ''}
                      </div>
                    )}
                  </>
                ) : (
                  !effectiveUsersLoading && <p>Нічого не знайдено</p>
                )}
              </div>
            </div>
          )}

            {activeTab === 'ads' && !listingsExpanded && (
              <div className="tab-panel">
                <div className="ads-results">
                  {effectiveAdsLoading && effectiveAds.length === 0 && renderAdsSkeletons(8)}
                  {effectiveAdsError && <div className="error">{effectiveAdsError}</div>}

                  {effectiveAds.length > 0 ? (
                    <>
                      <div className="ads-grid">
                        {renderAds.map((item, idx) => {
                          const currencyLabel = formatCurrencyLabel(item.currency || 'ГРН');

                          return (
                            <React.Fragment key={`ad-${String(item.listingId || item.id)}`}>
                            <div
                              className="ad-card glass-card pop-in"
                              style={{ animationDelay: `${0.01 * idx}s` }}
                              onClick={() => openListingTracked(item)}
                            >
                              <div className="ad-thumb">
                                {shouldShowBoostBadge(item) && (
                                  <span className="ad-boost-badge">Піднято</span>
                                )}
                                {(() => {
                                  const title = item.title || item.description || "Оголошення";
                                  const thumb = getListingThumb(item);
                                  const wanted = isWantedListing(item);
                                  const theme = getStablePosterTheme(item, title);
                                  const sizeClass = getPosterTitleSizeClass(title);

                                  if (thumb) {
                                    return (
                                      <img
                                        {...thumbImgProps(thumb, "(max-width: 600px) 50vw, (max-width: 1200px) 33vw, 320px")}
                                        alt={title}
                                        onError={fallbackToOriginal(thumb, DEFAULT_LISTING_IMG)}
                                      />
                                    );
                                  }

                                  if (wanted) {
                                    return (
                                      <div className={`ad-poster ad-poster--${theme}`}>
                                        <div className="ad-poster__glow" />
                                        <div className={`ad-poster__title ${sizeClass}`}>
                                          {title}
                                        </div>
                                      </div>
                                    );
                                  }

                                  return (
                                    <img
                                      src={DEFAULT_LISTING_IMG}
                                      alt={title}
                                    />
                                  );
                                })()}
                              </div>
                              <div className="ad-body">
                                <div className="ad-title">{item.title}</div>
                                <div className="ad-meta">
                                    {(() => {
                                      const listingType = String(item?.listingType || item?.type || "").toLowerCase();
                                      const t = String(item?.type || "").toLowerCase();
                                      const isWanted = listingType === "wanted" || t === "wanted";    


                                      if (isWanted) {
                                        return <span className="ad-badge ad-badge--wanted">Шукаю</span>;
                                      }

                                      const isExchange = t === "exchange" || !!item?.exchangeFor;
                                      if (isExchange) return <span className="ad-price">Обмін</span>;

                                      if (t === "free" || Number(item?.price) === 0) {
                                        return <span className="ad-price">Безкоштовно</span>;
                                      }

                                      return (
                                        <span className="ad-price">
                                          {typeof item.price === "number" ? `${item.price} ${currencyLabel}` : "—"}
                                        </span>
                                      );
                                    })()}
                                  <span className="ad-city">{item.location || ''}</span>
                                </div>
                              </div>
                            </div>

                            {idx === SEARCH_AD_INSERT_AFTER_IDX && (
                              <div className="search-results__ad-wide">
                                <AdSlot placement="search_results_inline" />
                              </div>
                            )}
                            </React.Fragment>
                          );
                        })}
                      </div>

                      {!agentResultSet && adsNextCursor && !adsReachedEnd && (
                      <div
                        ref={adsLoadMoreRef}
                        className="load-more-sentinel"
                        aria-hidden="true"
                      >
                        {adsLoading ? 'Завантаження…' : ''}
                      </div>
                    )}

                    {!agentResultSet && ads.length > 0 && adsReachedEnd && !adsLoading && (
                      <div className="load-more-sentinel">
                        На цьому поки все☺️
                      </div>
                    )}

                    {agentResultSet && (
                      <div ref={agentLoadMoreRef} className="load-more-sentinel" data-testid="agent-result-footer">
                        {agentResultSet.total > agentResultSet.listings.length
                          ? `Показано ${agentResultSet.listings.length} з ${agentResultSet.total}${agentLoadingMore ? ' · завантаження…' : ''}`
                          : `Знайдено: ${agentResultSet.total}`}
                      </div>
                    )}
                    </>
                  ) : (
                    !effectiveAdsLoading && <p>Нічого не знайдено</p>
                  )}
                </div>
              </div>
            )}

            {(activeTab === 'all' || (activeTab === 'ads' && listingsExpanded)) && (
              <div className="tab-panel">
                <div className="all-wrap-v2 fade-in">
                  <PeoplePreviewRow
                    users={effectiveUsers.slice(0, 12)}
                    loading={effectiveUsersLoading}
                    error={effectiveUsersError}
                    onOpenProfile={openProfileTracked}
                    onShowMore={effectiveUsers.length > 0 ? () => pushURL({ tab: 'users' }) : undefined}
                  />

                  <ListingsPreviewGrid
                    items={listingsExpanded || agentResultSet ? renderAds : renderAds.slice(0, 20)}
                    loading={effectiveAdsLoading}
                    error={effectiveAdsError}
                    onOpen={openListingTracked}
                    onShowMore={listingsExpanded || agentResultSet ? undefined : () => pushURL({ tab: 'ads' })}
                  />

                  {/* Phase A trigger — invisible sentinel just past the
                      capped preview. Intersecting it silently promotes
                      into the infinite continuation below (see the
                      IntersectionObserver effect gated on
                      `activeTab === 'all' && !listingsExpanded`). Only
                      rendered when there's demonstrably more to continue
                      into — never for a fixed, cursor-less Agent result set. */}
                  {!listingsExpanded && !agentResultSet && adsNextCursor && (
                    <div ref={allTabBottomSentinelRef} aria-hidden="true" />
                  )}

                  {/* §36/§44: the Agent result set is complete and honest — the real total, and the rest loads on scroll. */}
                  {agentResultSet && (
                    <div ref={agentLoadMoreRef} className="load-more-sentinel" data-testid="agent-result-footer">
                      {agentResultSet.total > agentResultSet.listings.length
                        ? `Показано ${agentResultSet.listings.length} з ${agentResultSet.total}${agentLoadingMore ? ' · завантаження…' : ''}`
                        : `Знайдено: ${agentResultSet.total}`}
                    </div>
                  )}

                  {/* Phase B — same pagination sentinel/end-of-list UI the
                      dedicated "Оголошення" tab already uses, just reachable
                      from here now that `activeTab` is 'ads'. */}
                  {listingsExpanded && adsNextCursor && !adsReachedEnd && (
                    <div
                      ref={adsLoadMoreRef}
                      className="load-more-sentinel"
                      aria-hidden="true"
                    >
                      {adsLoading ? 'Завантаження…' : ''}
                    </div>
                  )}

                  {listingsExpanded && ads.length > 0 && adsReachedEnd && !adsLoading && (
                    <div className="load-more-sentinel">
                      На цьому поки все☺️
                    </div>
                  )}

                  {/* Contextual Buyer Request — only once there's an active
                      query and it genuinely came up weak/empty, not as a
                      permanent idle-state CTA (per owner's redesign note),
                      and not while mid-infinite-scroll (doesn't make sense
                      positionally once already showing many results).
                      Suppressed while an Agent result set is active — the
                      agent already offers "Створити запит" itself on a zero-
                      result turn (composer.js's composeSearch), so this
                      would just be a duplicate CTA. */}
                  {!listingsExpanded &&
                    !agentResultSet &&
                    !!queryFromURL.trim() &&
                    !usersLoading &&
                    !adsLoading &&
                    filteredUsers.length === 0 &&
                    ads.length === 0 && (
                      <BuyerRequestSuggestion
                        onAction={() => navigate('/create-search')}
                      />
                    )}
                </div>
              </div>
            )}
          </div>
        </div>

        <FilterModal
          isOpen={isFilterOpen}
          category={category}
          initialFilters={attrFilters}
          initialGeneral={{ ...general, sort }}
          onApply={(preparedAttrs, preparedGeneral) => {
            setIsFilterOpen(false);
            applyFromModalToURL(preparedAttrs, preparedGeneral);
          }}
          onReset={() => {
            resetFilters();
          }}
          onClose={handleCloseFilters}
        />
      </div>
    </>
  );
};

export default SearchResults;
